import type { RefObject } from 'react';

/**
 * Creates a toggle handler that scrolls to the top of a section when collapsing.
 * Useful for "View More/View Less" buttons on details pages.
 *
 * @param isExpanded - Current expanded state
 * @param setExpanded - State setter function
 * @param sectionRef - Ref to the section element to scroll to
 * @param options - Optional configuration
 * @param options.onlyOnDesktop - If true, only scrolls when appDisplayMode is 'desktop'
 * @param options.appDisplayMode - Current app display mode ('mobile' | 'desktop')
 * @returns Toggle handler function
 */
// eslint-disable-next-line import/prefer-default-export
export function createSectionScrollOnCollapse<T extends HTMLElement>(
    isExpanded: boolean,
    setExpanded: (value: boolean | ((prev: boolean) => boolean)) => void,
    sectionRef: RefObject<T>,
    options?: {
        onlyOnDesktop?: boolean;
        appDisplayMode?: 'mobile' | 'desktop';
    }
) {
    return () => {
        const wasExpanded = isExpanded;

        // Autoscroll to top of section when clicking "View Less" on desktop
        if (wasExpanded && sectionRef.current) {
            const shouldScroll =
                !options?.onlyOnDesktop ||
                options?.appDisplayMode === 'desktop';

            if (shouldScroll) {
                const rect = sectionRef.current.getBoundingClientRect();
                const absoluteTop = rect.top + window.scrollY;

                window.scrollTo({
                    top: absoluteTop,
                    behavior: 'smooth',
                });
            }
        }
        setExpanded((prev) => !prev);
    };
}
